import type { SyntheticEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import FormatCurrency from 'components/Common/FormatCurrency'
import { CurrencyProvider } from 'contexts/currencyContext'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import Clickable from 'components/Common/Clickable'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import {
  type ManualItineraryDeleteRequest,
  type FlightSavedItemData,
  type TransportConnection,
  type TransportOption,
  isChatFlightSavedItemData,
  isCustomFlightSavedItemData,
  isFlightCardSavedItemData,
  isLeFlightSavedItemData,
  isLeCruiseSavedItemData,
} from 'tripPlanner/api/types'
import { useSwitchDefaultTransportMutation } from 'tripPlanner/hooks/api/chat'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TransportOptionItem, { getTransportIcon } from './TransportOptionItem'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import FlightSearchDrawer from './FlightSearchDrawer/FlightSearchDrawer'
import ShortlistedFlightsSection from './ShortlistedFlightsSection/ShortlistedFlightsSection'
import { isBookedFlightOption } from './ShortlistedFlightsSection/flightShortlistUtils'
import { getCachedJourney } from './ShortlistedFlightsSection/shortlistedJourneyCache'
import BookedFlightTile from './BookedFlightTile'
import { getSavedFlightBoundaryDateParts } from './bookedFlightTileUtils'
import { CANVAS_CONTENT_WIDTH, PLACEHOLDER_TILE_BORDER_WIDTH } from './constants'
import TripPlanTileDashedOutline, { tripPlanTileOutlineHoverStyles } from './TripPlanTileDashedOutline'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { getFlightSearchDrawerParamsFromTransport } from './FlightSearchDrawer/flightSearchDrawerParams'

const FLIGHT_TILE_ICON_BACKGROUND = 'rgba(27, 89, 248, 0.2)'
const FLIGHT_TILE_ICON_COLOUR = '#1B59F8'

const TransportContainer = styled.div`
  max-width: var(--itinerary-canvas-width, ${rem(CANVAS_CONTENT_WIDTH)});
  width: 100%;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(20)};
`

const TransportContent = styled(Group)`
  min-width: 0;
`

const TransportTile = styled.div<{ $clickable?: boolean; $dashed?: boolean }>`
  position: relative;
  border: ${rem(PLACEHOLDER_TILE_BORDER_WIDTH)} solid ${props => (props.$dashed ? 'transparent' : props.theme.palette.neutral.surface.lightForeground)};
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(12)} ${rem(14)};
  ${tripPlanTileOutlineHoverStyles}

  ${props => props.$clickable && css`
    cursor: pointer;

    &:hover {
      border-color: ${props.$dashed ? 'transparent' : props.theme.palette.neutral.surface.lightForegroundAlt};
      box-shadow: ${props.theme.shadow.flat.small};
    }
  `}
`

const SavedCountLabel = styled(Label)``

const MultiCityTotalLabel = styled(Label)`
  align-self: flex-start;
`

const SavedFlightMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

const SavedFlightMetaItem = styled.div`
  display: inline-flex;
  align-items: center;

  & + &::before {
    content: '·';
    margin: 0 ${rem(6)};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const PricePrefix = styled.span`
  margin-inline-end: ${rem(4)};
`

const SavedFlightLuxPlusPrice = styled(PriceRowLuxPlusPrice)`
  display: inline-flex;
`

const IconBadge = styled.div`
  width: ${rem(40)};
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: ${rem(10)};
  background: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.lightForegroundAlt}, transparent 80%);
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-size: ${rem(20)};

  &.flight {
    background: ${FLIGHT_TILE_ICON_BACKGROUND};
    color: ${FLIGHT_TILE_ICON_COLOUR};
  }
`

const BrowseOffersButton = styled(TextButton)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(8)} ${rem(12)};
  line-height: 1;

  &:hover,
  &:focus-visible {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

function formatDuration(option: TransportOption): string {
  const duration = option.duration?.trim()
  if (!duration) return ''
  const normalizedDuration = duration.replace(/^~+\s*/, '')
  return ` ~${normalizedDuration}`
}

export function getFlightTransportDropdownOptions(options: ReadonlyArray<TransportOption>): Array<TransportOption> {
  return options.filter(o => !isFlightCardSavedItemData(o.savedItemData) && !isLeCruiseSavedItemData(o.savedItemData))
}

function isFlightOptionType(option: TransportOption): boolean {
  const normalised = option.type.toLowerCase().replace(/[_\s]/g, '')
  return normalised === 'flight' || normalised === 'leflight' || normalised === 'chatflight'
}

function formatSavedFlightDateLabel(
  data: FlightSavedItemData | undefined,
  boundary: 'departure' | 'arrival',
  intl: ReturnType<typeof useIntl>,
): string | undefined {
  const parts = getSavedFlightBoundaryDateParts(data, boundary)
  if (!parts) return undefined
  return intl.formatDate(new Date(parts.year, parts.month - 1, parts.day), { weekday: 'short', day: 'numeric', month: 'short' })
}

function getSavedFlightSummaryDateLabel(
  data: FlightSavedItemData | undefined,
  isReturn: boolean,
  reversed: boolean,
  intl: ReturnType<typeof useIntl>,
): string | undefined {
  if (!data) return undefined

  if (data.flightGroupType === 'multi_city') {
    const departureDate = formatSavedFlightDateLabel(data, 'departure', intl)
    const arrivalDate = formatSavedFlightDateLabel(data, 'arrival', intl)
    if (departureDate && arrivalDate && departureDate !== arrivalDate) return `${departureDate} - ${arrivalDate}`
    return departureDate ?? arrivalDate
  }

  return formatSavedFlightDateLabel(data, isReturn && reversed ? 'departure' : 'arrival', intl)
}

interface Props {
  transport: TransportConnection
  onRefetchItinerary?: () => void
  onSendMessage?: (message: string) => void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest) => Promise<void> | void
  /** Render the return leg of a booked return flight at the end of the itinerary (reversed direction). */
  reversed?: boolean
  /** Heading override (e.g. the reversed "Paris → Sydney" for the return tile). Defaults to transport.name. */
  title?: string
}

function FlightTripPlanTransportLine({ transport, onRefetchItinerary = noop, onSendMessage, onDeleteTarget, reversed = false, title }: Props) {
  const intl = useIntl()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isFlightDrawerOpen, setIsFlightDrawerOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { mutate: switchDefault, isPending: isSwitching } = useSwitchDefaultTransportMutation()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const actionsDisabled = isSwitching || isStreaming || isStreamOpen || isManualItineraryDeletePending

  const defaultOption = useMemo((): TransportOption => {
    const explicitDefault = transport.options.find((o) => o.savedItemData?.isDefaultTransport === true)
    if (explicitDefault && isFlightOptionType(explicitDefault)) return explicitDefault
    // Keep flight legs showing a flight rather than flipping to another mode (e.g. train).
    return transport.options.find(isFlightOptionType) ?? explicitDefault ?? transport.options[0]
  }, [transport.options])

  const toggleDropdown = useCallback((event?: SyntheticEvent) => {
    event?.stopPropagation()
    if (actionsDisabled) return
    setIsDropdownOpen(prev => !prev)
  }, [actionsDisabled])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleOptionClick = useCallback((itemId: number) => {
    closeDropdown()
    if (itemId === defaultOption.itemId) return

    switchDefault(
      { itemId },
      {
        onSuccess: onRefetchItinerary,
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: '03GxTY2YYsWsZc8Yxf64',
            defaultMessage: 'Failed to switch transport. Please try again.',
            description: 'Error snackbar message when switching the default transport option fails',
          }), 'critical')
        },
      },
    )
  }, [defaultOption.itemId, closeDropdown, switchDefault, onRefetchItinerary, intl])

  const handleDeleteItem = useCallback((itemId: number) => {
    if (actionsDisabled) return
    onDeleteTarget?.({ targetType: 'item', itemId })
  }, [actionsDisabled, onDeleteTarget])

  const shortlistedFlightsCount = useMemo(
    () => transport.options.filter(o => isFlightCardSavedItemData(o.savedItemData)).length,
    [transport.options],
  )
  const hasShortlistedFlights = shortlistedFlightsCount > 0

  const bookedFlightOption = useMemo(
    () => transport.options.find(o => isFlightCardSavedItemData(o.savedItemData) && isBookedFlightOption(o)),
    [transport.options],
  )
  const otherSavedFlightOptions = useMemo(
    () => transport.options.filter(o => isFlightCardSavedItemData(o.savedItemData) && !isBookedFlightOption(o)),
    [transport.options],
  )

  const handleDeleteBookedFlight = useCallback(() => {
    if (bookedFlightOption) handleDeleteItem(bookedFlightOption.itemId)
  }, [bookedFlightOption, handleDeleteItem])

  const headingName = title ?? transport.name

  const dropdownOptions = useMemo(
    () => getFlightTransportDropdownOptions(transport.options),
    [transport.options],
  )
  const hasMultipleOptions = dropdownOptions.length > 1
  const typeLabel = defaultOption.label ?? defaultOption.type
  const DropdownIcon = isDropdownOpen ? LineAngleUpIcon : LineAngleDownIcon
  const normalizedType = defaultOption.type.toLowerCase().replace(/[_\s]/g, '')
  const normalizedTypeLabel = typeLabel.toLowerCase().replace(/[_\s]/g, '')
  const isFlightTransport = normalizedType === 'flight' ||
    normalizedType === 'leflight' ||
    normalizedType === 'chatflight' ||
    normalizedTypeLabel === 'flight'
  const defaultOptionSavedItemData = defaultOption.savedItemData
  const defaultFlightSavedItemData =
    isLeFlightSavedItemData(defaultOptionSavedItemData) ||
    isChatFlightSavedItemData(defaultOptionSavedItemData) ||
    isCustomFlightSavedItemData(defaultOptionSavedItemData)
      ? defaultOptionSavedItemData
      : undefined
  const flightSearchDrawerParams = getFlightSearchDrawerParamsFromTransport(defaultOption, transport)

  const openFlightDrawer = useCallback(() => {
    setIsFlightDrawerOpen(true)
  }, [])

  const closeFlightDrawer = useCallback(() => {
    setIsFlightDrawerOpen(false)
  }, [])

  const browseOffersLabel = intl.formatMessage({
    id: 'h73rmlEmHCI8JbPRYjch',
    defaultMessage: 'Search flights',
    description: 'Button label to open the flight offers/search drawer from the transport line in trip planner',
  })

  const savedFlightsCountLabel = intl.formatMessage({
    id: 'wk7-MRk4wFyulX5cpkTF',
    defaultMessage: '{count, plural, one {# saved} other {# saved}}',
    description: 'Pill label showing how many flights are saved/shortlisted on a transport leg in trip planner',
  }, { count: shortlistedFlightsCount })

  const label = (
    <BodyText variant="small">
      <BodyText variant="small" weight="bold" as="span">{typeLabel}</BodyText>
      {formatDuration(defaultOption)}
    </BodyText>
  )

  const savedFlightOption = useMemo(
    () => transport.options.find(o => isFlightCardSavedItemData(o.savedItemData)),
    [transport.options],
  )
  const savedFlightData = savedFlightOption && isFlightCardSavedItemData(savedFlightOption.savedItemData)
    ? savedFlightOption.savedItemData
    : undefined

  // Prefer a shortlisted flight's data; otherwise fall back to the default flight option so the
  // tile still shows the airport-code route (e.g. "SYD → CDG") rather than a generic label.
  const summaryFlightData = savedFlightData ?? defaultFlightSavedItemData
  const summaryFlightRoute = summaryFlightData?.departureAirport && summaryFlightData.arrivalAirport
    ? `${summaryFlightData.departureAirport} → ${summaryFlightData.arrivalAirport}`
    : ''
  const savedFlightIsReturn = Boolean(savedFlightOption?.returnLeg ?? defaultOption.returnLeg)
  const savedFlightPrice = summaryFlightData?.farePrice ?? summaryFlightData?.price
  const savedFlightCurrency = summaryFlightData?.fareCurrencyCode ?? summaryFlightData?.currencyCode
  const savedFlightRegularPrice = summaryFlightData?.isLuxPlusFare === true
    ? summaryFlightData.regularPrice
    : undefined
  const showSavedFlightLuxPlusPrice = savedFlightPrice != null &&
    savedFlightCurrency &&
    savedFlightRegularPrice !== undefined &&
    Number.isFinite(savedFlightRegularPrice)
  // Multi-city legs are saved as separate flights sharing one group; surface the
  // combined itinerary total (stored per leg) rather than letting the per-leg
  // "From" prices read as the whole trip's flight cost. The combined total is
  // path-dependent and is not the sum of the per-leg fares.
  const multiCityGroupTotal = summaryFlightData?.flightGroupType === 'multi_city'
    ? summaryFlightData.flightGroupTotalPrice
    : undefined
  const multiCityGroupCurrency = summaryFlightData?.flightGroupTotalCurrencyCode ?? savedFlightCurrency
  const savedFlightDuration = summaryFlightData?.duration
  const savedFlightDateLabel = getSavedFlightSummaryDateLabel(summaryFlightData, savedFlightIsReturn, reversed, intl)
  // Stop count: the cached journey is the source of truth; fall back to the saved layover list.
  // Left undefined (rather than 0) when unknown, so we never show a misleading "Direct".
  const cachedJourney = summaryFlightData?.journeyKey ? getCachedJourney(summaryFlightData.journeyKey)?.journey : undefined
  const savedFlightStops = cachedJourney?.flightGroup?.numberOfStops ?? summaryFlightData?.layovers?.length

  const savedFlightPricePrefix = intl.formatMessage(savedFlightIsReturn
    ? { id: 'tripPlanSavedFlightReturnFrom', defaultMessage: 'Return from', description: 'Saved return flight price prefix in trip planner transport tile' }
    : { id: 'tripPlanSavedFlightFrom', defaultMessage: 'From', description: 'Saved one-way flight price prefix in trip planner transport tile' })
  const savedFlightPerAdult = intl.formatMessage({
    id: 'Bg2W8PA6j_4tS-XFYnVD',
    defaultMessage: '/adult',
    description: 'Per-adult price suffix on the saved flight summary in the trip planner transport tile',
  })
  const savedFlightStopsLabel = savedFlightStops != null
    ? intl.formatMessage({
        id: '2ZlV36qOfRUhSIJ_2cPy',
        defaultMessage: '{count, plural, =0 {Direct} one {# stop} other {# stops}}',
        description: 'Stop count on the saved flight summary in the trip planner transport tile',
      }, { count: savedFlightStops })
    : undefined

  const savedFlightSummary = summaryFlightRoute ? (
    <Group direction="vertical" gap={2}>
      <BodyText variant="small" weight="bold">{summaryFlightRoute}</BodyText>
      <SavedFlightMeta>
        {savedFlightPrice != null && savedFlightCurrency && (
          <SavedFlightMetaItem>
            {showSavedFlightLuxPlusPrice ? (
              <>
                <BodyText variant="small" colour="neutral-two" as="span">
                  <PricePrefix>{savedFlightPricePrefix}</PricePrefix>
                </BodyText>
                <CurrencyProvider currency={savedFlightCurrency}>
                  <SavedFlightLuxPlusPrice
                    size="S"
                    memberPrice={savedFlightPrice}
                    nonMemberPrice={savedFlightRegularPrice}
                    saleUnit="adult"
                  />
                </CurrencyProvider>
              </>
            ) : (
              <BodyText variant="small" colour="neutral-two" as="span">
                <PricePrefix>{savedFlightPricePrefix}</PricePrefix>
                <CurrencyProvider currency={savedFlightCurrency}>
                  <FormatCurrency value={savedFlightPrice} format="roundedDollar" />
                </CurrencyProvider>
                {savedFlightPerAdult}
              </BodyText>
            )}
          </SavedFlightMetaItem>
        )}
        {savedFlightDateLabel && (
          <SavedFlightMetaItem>
            <BodyText variant="small" colour="neutral-two">{savedFlightDateLabel}</BodyText>
          </SavedFlightMetaItem>
        )}
        {savedFlightDuration && (
          <SavedFlightMetaItem>
            <BodyText variant="small" colour="neutral-two">{savedFlightDuration}</BodyText>
          </SavedFlightMetaItem>
        )}
        {savedFlightStopsLabel && (
          <SavedFlightMetaItem>
            <BodyText variant="small" colour="neutral-two">{savedFlightStopsLabel}</BodyText>
          </SavedFlightMetaItem>
        )}
      </SavedFlightMeta>
      {multiCityGroupTotal != null && multiCityGroupCurrency && (
        <MultiCityTotalLabel kind="tertiary" variant="grey">
          <PricePrefix>
            {intl.formatMessage({
              id: '2qaZYohv3dcAe2-f6tzs',
              defaultMessage: 'Multi-city total',
              description: 'Badge label for the combined price of a multi-city flight itinerary in the trip planner transport tile',
            })}
          </PricePrefix>
          <CurrencyProvider currency={multiCityGroupCurrency}>
            <FormatCurrency value={multiCityGroupTotal} format="roundedDollar" />
          </CurrencyProvider>
        </MultiCityTotalLabel>
      )}
    </Group>
  ) : null

  const savedCountOrBrowseButton = hasShortlistedFlights ? (
    <SavedCountLabel kind="tertiary" variant="success">
      {savedFlightsCountLabel}
    </SavedCountLabel>
  ) : (
    <BrowseOffersButton
      kind="tertiary"
      size="small"
      startIcon={<LineSearchIcon />}
      onClick={openFlightDrawer}
    >
      {browseOffersLabel}
    </BrowseOffersButton>
  )

  const transportTile = (
    <TransportTile
      $clickable={isFlightTransport}
      $dashed={!hasShortlistedFlights}
      onClick={isFlightTransport ? openFlightDrawer : undefined}
    >
      {!hasShortlistedFlights && <TripPlanTileDashedOutline radius="M" />}
      <Group direction="horizontal" gap={12} verticalAlign="center">
        <IconBadge className={isFlightTransport ? 'flight' : undefined}>
          {isFlightTransport ? <LinePlaneFlyIcon size="S" /> : getTransportIcon(defaultOption.type, 'S')}
        </IconBadge>
        <TransportContent direction="vertical" gap={4} fullWidth>
          {savedFlightSummary && savedFlightSummary}
          {!savedFlightSummary && !hasMultipleOptions && label}
          {!savedFlightSummary && hasMultipleOptions && <>
            <Clickable ref={triggerRef} onClick={toggleDropdown} disabled={actionsDisabled}>
              <Group direction="horizontal" gap={2} verticalAlign="center">
                {label}
                <DropdownIcon size="S" />
              </Group>
            </Clickable>
            <DropdownList
              size="S"
              open={isDropdownOpen}
              onClose={closeDropdown}
              triggerRef={triggerRef}
              anchorRef={triggerRef}
              portaled
              placement="bottom-start"
            >
              {dropdownOptions.map((option) => (
                <TransportOptionItem
                  key={option.itemId}
                  option={option}
                  isSelected={option.itemId === defaultOption.itemId}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </DropdownList>
          </>}
        </TransportContent>
        {isFlightTransport && (
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {savedCountOrBrowseButton}
          </Group>
        )}
      </Group>
    </TransportTile>
  )

  const bookedFlightTileNode = bookedFlightOption ? (
    <BookedFlightTile
      option={bookedFlightOption}
      otherSavedOptions={otherSavedFlightOptions}
      onOpenDrawer={openFlightDrawer}
      onDelete={handleDeleteBookedFlight}
      reversed={reversed}
    />
  ) : null

  return (
    <TransportContainer>
      <Group direction="vertical" gap={12} fullWidth>
        <Heading variant="heading5" as="span">{headingName}</Heading>
        {bookedFlightOption ? bookedFlightTileNode : transportTile}
      </Group>
      {isFlightTransport && (
        <FlightSearchDrawer
          isOpen={isFlightDrawerOpen}
          onClose={closeFlightDrawer}
          originAirportCode={flightSearchDrawerParams.originAirportCode}
          originAirportName={flightSearchDrawerParams.originAirportName}
          destinationAirportCode={flightSearchDrawerParams.destinationAirportCode}
          destinationAirportName={flightSearchDrawerParams.destinationAirportName}
          departureDate={flightSearchDrawerParams.departureDate}
          showMultiCity
          onSendMessage={onSendMessage}
          isStreaming={isStreaming}
          existingFlights={transport.options}
          hasSavedFlights={hasShortlistedFlights}
          headerSlot={hasShortlistedFlights
            ? <ShortlistedFlightsSection transport={transport} onDeleteItem={handleDeleteItem} hideHeader />
            : undefined}
        />
      )}
    </TransportContainer>
  )
}

export default FlightTripPlanTransportLine
